import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useStore } from "../../../../stores/StoreContext";

import "./CostOverviewTab.scss";

import { observer } from "mobx-react-lite";
import { throttle } from "lodash";

import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";

import { AppRole, BudgetableUserReadModel } from "../../../../../@api";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useCurrentUserService, useUserManagementService } from "../../../../services/ServiceHooks";
import { TextMarkdown } from "../../../Shared/Components";
import UserTable from "./UserTable";

const CostOverviewTabRaw: FunctionComponent = () => {
  const { permissionStore, budgetStore } = useStore();
  const userManagementService = useUserManagementService();
  const currentUserService = useCurrentUserService();
  const { t } = useTranslation();
  const [users, setUsers] = useState<BudgetableUserReadModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [displayBudget, setDisplayBudget] = useState<boolean>(budgetStore.budgetInfoValue.displayBudget);

  const areaTranslationKeys = translationKeys.adminArea.costOverview.areas;

  const requestUsers = async () => {
    setIsLoading(true);

    const response: BudgetableUserReadModel[] = await userManagementService.getAllUserData();
    setUsers(response);

    setIsLoading(false);
  };

  useEffect(() => {
    if (!users?.length && permissionStore.hasRole(AppRole.UserManagementAdmin)) {
      requestUsers();
    }
  }, []);

  const updateBudgetSetting = useCallback(
    throttle(async value => {
      await userManagementService.updateSetDisplayBudget(value);
      currentUserService.ensureHasBudget();
    }, 500),
    [],
  );

  const handleDisplayBudgetChange = (value: boolean) => {
    setDisplayBudget(value);
    updateBudgetSetting(value);
  };

  const displayBudgetToggleSettingMemo = useMemo(
    () => (
      <div className="flex flex-row align-items-center gap-4">
        <div className="toggle-label">{t(areaTranslationKeys.budgetSettings.toggleTitle)}</div>
        <InputSwitch onClick={e => handleDisplayBudgetChange(!displayBudget)} checked={displayBudget} />
      </div>
    ),
    [displayBudget],
  );

  return (
    <div className="cost-overview-tab">
      <Card title={t(areaTranslationKeys.costOverview.header)}>
        <TextMarkdown>{t(areaTranslationKeys.costOverview.descriptionMarkdown)}</TextMarkdown>

        <UserTable users={users} isLoading={isLoading} />
      </Card>

      <Card title={t(areaTranslationKeys.budgetSettings.header)}>
        <TextMarkdown>{t(areaTranslationKeys.budgetSettings.descriptionMarkdown)}</TextMarkdown>

        {displayBudgetToggleSettingMemo}
      </Card>
    </div>
  );
};

export const CostOverviewTab = observer(CostOverviewTabRaw);
